<template>
  <div id="contacts-import">
    <div class="card">
      <div class="card-content">
        <div class="level">
          <div class="level-left">
            <div>
              <h1 class="title is-4">Importar Excel</h1>
              <p class="subtitle is-6">Administrar importación de contactos.</p>
            </div>
          </div>

          <div class="level-right">
            <b-button
              tag="router-link"
              to="/contactos"
              type="is-light"
              icon-pack="fas"
              icon-left="arrow-left"
            >
              Volver
            </b-button>
          </div>
        </div>

        <hr />

        <div class="steps-wrapper">
          <div
            v-for="(item, index) in steps"
            :key="index"
            class="step-item"
            :class="{
              'is-active': step === index,
              'is-completed': step > index,
            }"
          >
            <div class="step-number">
              {{ index + 1 }}
            </div>

            <div class="step-label">
              {{ item }}
            </div>
          </div>
        </div>

        <hr />
        <hr />

        <div class="import-step-content">
          <ContactImportUploadStep
            v-if="step === 0"
            :file="file"
            @selected="onFileSelected"
            @download-template="downloadTemplate"
          />

          <ContactImportValidateStep
            v-if="step === 1"
            :file="file"
            :validation="validation"
            :loading="validating"
            @validate="validateFile"
          />

          <ContactImportPreviewStep
            v-if="step === 2"
            :rows="rows"
            :validation="validation"
          />

          <ContactImportProcessStep
            <ContactImportProcessStep
            v-if="step === 3"
            :file="file"
            :total-rows="totalRows"
            :can-import="canImport"
            :importing="importing"
            :submitted="submitted"
            :import-error="importError"
            :import-status="importStatus"
            :import-message="importMessage"
            @start-import="startImport"
          />
        </div>

        <div class="buttons mt-4 import-actions-fixed">
          <b-button
            v-if="step > 0"
            type="is-light"
            icon-pack="fas"
            icon-left="arrow-left"
            @click="previousStep"
          >
            Atrás
          </b-button>

          <b-button
            v-if="step < 3"
            type="is-primary"
            icon-pack="fas"
            icon-right="arrow-right"
            :disabled="!canGoNext"
            @click="nextStep"
          >
            Siguiente
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

import ContactImportUploadStep from "@/components/ContactImportUploadStep.vue";
import ContactImportValidateStep from "@/components/ContactImportValidateStep.vue";
import ContactImportPreviewStep from "@/components/ContactImportPreviewStep.vue";
import ContactImportProcessStep from "@/components/ContactImportProcessStep.vue";

export default {
  name: "ContactImport",

  components: {
    ContactImportUploadStep,
    ContactImportValidateStep,
    ContactImportPreviewStep,
    ContactImportProcessStep,
  },

  data() {
    return {
      baseURL: process.env.VUE_APP_ROOT_API,

      step: 0,

      steps: ["Archivo", "Validación", "Datos", "Finalizar"],

      file: null,
      rows: [],
      previewRows: [],
      validation: null,

      validating: false,
      importing: false,
      submitted: false,
      importError: "",

      importId: null,
      importStatus: "",
      importMessage: "",
      importPolling: null,
    };
  },

  computed: {
    totalRows() {
      return this.rows.length;
    },

    canImport() {
      return this.file && this.validation && this.validation.isValid;
    },

    canGoNext() {
      if (this.step === 0) {
        return !!this.file;
      }

      if (this.step === 1) {
        return !!this.validation && this.validation.isValid;
      }

      if (this.step === 2) {
        return this.canImport;
      }

      return false;
    },
  },

  methods: {
    onFileSelected(file) {
      this.file = file;
      this.rows = [];
      this.previewRows = [];
      this.validation = null;
      this.submitted = false;
      this.importError = "";
    },

    beforeDestroy() {
      this.stopImportPolling();
    },

    nextStep() {
      if (this.canGoNext) {
        this.step++;
      }
    },

    previousStep() {
      if (this.step > 0) {
        this.step--;
      }
    },

    async validateFile() {
      if (!this.file) {
        return;
      }

      this.validating = true;
      this.validation = null;
      this.rows = [];
      this.previewRows = [];

      try {
        const workbook = await this.readWorkbook(this.file);
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const rawRows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          raw: false,
        });

        const headers = rawRows[0] || [];
        const dataRows = rawRows
          .slice(1)
          .filter((row) => row.some((cell) => String(cell).trim() !== ""));

        const expectedColumns = this.getExpectedColumns();

        const columns = expectedColumns.map((column, index) => {
          const detected = headers[index] || "";

          return {
            key: column.key,
            label: column.label,
            required: column.required,
            found:
              this.normalizeHeader(detected) ===
              this.normalizeHeader(column.label),
            detected,
          };
        });

        const errors = [];

        columns.forEach((column) => {
          if (column.required && !column.found) {
            errors.push(
              `La columna ${column.label} no coincide. Se detectó: ${
                column.detected || "vacío"
              }`
            );
          }
        });

        this.rows = dataRows.map((row, index) => this.mapRow(row, index + 2));

        const invalidDniRows = this.rows.filter((row) => row.errors.length);

        if (invalidDniRows.length > 0) {
          errors.push(
            `Hay ${invalidDniRows.length} filas con DNI inválido. Esas filas no deberían importarse.`
          );
        }

        this.previewRows = this.rows.slice(0, 20);

        this.validation = {
          isValid: errors.length === 0,
          totalRows: this.rows.length,
          detectedColumns: headers.length,
          columns,
          errors,
          warnings:
            this.rows.length > 20
              ? [
                  `Se muestran solo las primeras 20 filas de ${this.rows.length}.`,
                ]
              : [],
        };

        if (this.validation.isValid) {
          this.$buefy.toast.open({
            message: "Archivo validado correctamente.",
            type: "is-success",
          });
        } else {
          this.$buefy.toast.open({
            message: "El archivo tiene errores.",
            type: "is-danger",
          });
        }
      } catch (error) {
        this.validation = {
          isValid: false,
          totalRows: 0,
          detectedColumns: 0,
          columns: [],
          errors: [error.message],
          warnings: [],
        };

        this.$buefy.toast.open({
          message: error.message,
          type: "is-danger",
        });
      } finally {
        this.validating = false;
      }
    },

    readWorkbook(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            resolve(workbook);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = () => {
          reject(new Error("No se pudo leer el archivo."));
        };

        reader.readAsArrayBuffer(file);
      });
    },

    getExpectedColumns() {
      return [
        { key: "delegation", label: "Delegación", required: true },
        { key: "num_affiliate", label: "N° afiliado", required: true },
        { key: "plan", label: "Plan", required: true },
        { key: "fullname", label: "Nombre completo", required: true },
        { key: "dni", label: "DNI", required: true },
        { key: "date_birth", label: "Fecha nacimiento", required: true },
        { key: "cuil", label: "CUIL", required: false },
        { key: "province", label: "Provincia", required: false },
        { key: "city", label: "Ciudad", required: false },
        { key: "address", label: "Dirección", required: false },
        { key: "phone", label: "Teléfono", required: false },
        { key: "celphone", label: "Celular", required: false },
        { key: "email", label: "Email", required: false },
        { key: "nacionality", label: "Nacionalidad", required: false },
        { key: "relationship", label: "Parentesco", required: false },
        { key: "work", label: "Trabajo", required: false },
        { key: "civil_status", label: "Estado civil", required: false },
      ];
    },

    mapRow(row, rowNumber) {
      const dni = this.cleanDni(row[4]);

      const errors = [];

      if (!dni) {
        errors.push("DNI inválido");
      }

      return {
        row: rowNumber,
        delegation: row[0] || "",
        num_affiliate: row[1] || "",
        plan: row[2] || "",
        fullname: row[3] || "",
        dni,
        date_birth: row[5] || "",
        cuil: row[6] || "",
        province: row[7] || "",
        city: row[8] || "",
        address: row[9] || "",
        phone: row[10] || "",
        celphone: row[11] || "",
        email: row[12] || "",
        nacionality: row[13] || "",
        relationship: row[14] || "",
        work: row[15] || "",
        civil_status: row[16] || "",
        errors,
      };
    },

    cleanDni(value) {
      const raw = String(value || "").trim();
      const matches = raw.match(/(\d{6,})/);

      return matches ? matches[1] : "";
    },

    normalizeHeader(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace("nº", "n°");
    },

    async startImport() {
      if (!this.canImport || this.importing || this.submitted) {
        return;
      }

      this.importing = true;
      this.importError = "";
      this.importStatus = "";
      this.importMessage = "";
      this.importId = null;

      const formData = new FormData();
      formData.append("file", this.file);

      try {
        const token = this.getToken();

        if (!token) {
          throw new Error(
            "No se encontró el token de sesión. Volvé a iniciar sesión."
          );
        }

        const url = this.apiUrl("/api/import-contacts");

        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
          body: formData,
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || data.message || response.statusText);
        }

        this.submitted = true;
        this.importId = data.import_id || null;
        this.importStatus = "queued";
        this.importMessage =
          data.message ||
          "Importación encolada correctamente. Se procesará en segundo plano.";

        this.$buefy.toast.open({
          message: this.importMessage,
          type: "is-success",
        });

        if (this.importId) {
          this.startImportPolling();
        }
      } catch (error) {
        this.importError = error.message;
        this.importStatus = "failed";
        this.importMessage = error.message;

        this.$buefy.toast.open({
          message: "Error al importar: " + error.message,
          type: "is-danger",
        });
      } finally {
        this.importing = false;
      }
    },

    startImportPolling() {
      this.stopImportPolling();

      this.checkImportStatus();

      this.importPolling = setInterval(() => {
        this.checkImportStatus();
      }, 2000);
    },

    stopImportPolling() {
      if (this.importPolling) {
        clearInterval(this.importPolling);
        this.importPolling = null;
      }
    },

    async checkImportStatus() {
      if (!this.importId) {
        return;
      }

      try {
        const token = this.getToken();

        if (!token) {
          this.stopImportPolling();
          return;
        }

        const url = this.apiUrl(`/api/import-contacts/status/${this.importId}`);

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          return;
        }

        this.importStatus = data.status || "";
        this.importMessage = data.message || "";

        if (this.importStatus === "completed") {
          this.stopImportPolling();

          this.$buefy.toast.open({
            message:
              this.importMessage || "Importación procesada correctamente.",
            type: "is-success",
          });
        }

        if (this.importStatus === "failed") {
          this.stopImportPolling();

          this.importError = this.importMessage || "La importación falló.";

          this.$buefy.toast.open({
            message: "Error al procesar importación: " + this.importError,
            type: "is-danger",
          });
        }
      } catch (error) {
        console.log(error);
      }
    },

    apiUrl(path) {
      const base = String(this.baseURL || "").replace(/\/+$/, "");

      if (base.endsWith("/api") && path.indexOf("/api/") === 0) {
        return base + path.replace(/^\/api/, "");
      }

      return base + path;
    },

    getToken() {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token") || "";

      return token.replace(/^"|"$/g, "");
    },

    downloadTemplate() {
      const headers = [
        "Delegación",
        "N° afiliado",
        "Plan",
        "Nombre completo",
        "DNI",
        "Fecha nacimiento",
        "CUIL",
        "Provincia",
        "Ciudad",
        "Dirección",
        "Teléfono",
        "Celular",
        "Email",
        "Nacionalidad",
        "Parentesco",
        "Trabajo",
        "Estado civil",
      ];

      const example = [
        "Cruz Azul",
        "12345",
        "PMO",
        "Juan Carlos Pérez",
        "30111222",
        "15/04/1988",
        "20-30111222-3",
        "Entre Ríos",
        "Paraná",
        "San Martín 123",
        "3434567890",
        "3435123456",
        "juan.perez@email.com",
        "Argentina",
        "T",
        "Relación de dependencia",
        "Soltero",
      ];

      const relationshipCodes = [
        ["Código", "Parentesco"],
        ["T", "Titular"],
        ["H", "Hijo/a"],
        ["C", "Cónyuge"],
        ["CO", "Concubino/a"],
        ["D", "Discapacitado"],
        ["FC", "Familiar a Cargo"],
        ["HC", "Hijo/a cónyuge"],
        ["M", "Menor bajo tutela"],
      ];

      const ws = XLSX.utils.aoa_to_sheet([headers, example]);
      const wsCodes = XLSX.utils.aoa_to_sheet(relationshipCodes);

      ws["!cols"] = [
        { wch: 18 },
        { wch: 15 },
        { wch: 15 },
        { wch: 28 },
        { wch: 14 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 28 },
        { wch: 16 },
        { wch: 16 },
        { wch: 28 },
        { wch: 18 },
        { wch: 14 },
        { wch: 26 },
        { wch: 18 },
      ];

      wsCodes["!cols"] = [{ wch: 12 }, { wch: 25 }];

      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, "Plantilla");
      XLSX.utils.book_append_sheet(wb, wsCodes, "Parentescos");

      XLSX.writeFile(wb, "plantilla_importar_contactos.xlsx");
    },
  },
};
</script>

<style scoped>
.mt-4 {
  margin-top: 1rem;
}

.steps-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.step-item {
  flex: 1;
  text-align: center;
  position: relative;
  color: #999;
}

.step-item:not(:last-child)::after {
  content: "";
  position: absolute;
  top: 18px;
  right: -50%;
  width: 100%;
  height: 2px;
  background: #dbdbdb;
  z-index: 0;
}

.step-number {
  width: 36px;
  height: 36px;
  margin: 0 auto 8px;
  border-radius: 50%;
  background: #dbdbdb;
  color: #363636;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  position: relative;
  z-index: 1;
}

.step-label {
  font-size: 13px;
  font-weight: 600;
}

.step-item.is-active {
  color: #3273dc;
}

.step-item.is-active .step-number {
  background: #3273dc;
  color: #fff;
}

.step-item.is-completed {
  color: #48c774;
}

.step-item.is-completed .step-number {
  background: #48c774;
  color: #fff;
}

.step-item.is-completed:not(:last-child)::after {
  background: #48c774;
}

#contacts-import {
  padding-bottom: 90px;
}

.import-actions-fixed {
  position: fixed;
  left: 70px;
  right: 0;
  bottom: 0;
  z-index: 100;
  background: #ffffff;
  padding: 12px 24px;
  margin: 0;
  border-top: 1px solid #ededed;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}
</style>